const codes = [
  // Client-only Values
  'unknown',

  // API Values
  'user_not_found',
  'auth_user_expired',
  'invalid_request_error',
  'api_error',
  'aws_error',
  'record_conflict',
  'auth_user_expired',
  'company_not_found',
  'account_already_exists',
  'user_api_permission',
  'not_found',
  'user_api_password_reset',
  'company_client_not_found',
  'custom_fields_not_found',
  'parameter_invalid_empty',
  'esig_request_invalid',
  'tags_perm_err',
  'billing_no_customer',
  'billing_no_active_customer_subscription',
  'billing_no_subscription',
  'billing_no_subscription_items',
  'billing_no_bank_connected',
  'billing_no_valid_bank_connected',
  'billing_no_payment_method',
  'billing_card_declined',
  'articles_perm_err',
  'domains_settings_api_err',
  'domains_settings_api_invalid_domain',
  'domains_settings_duplicate_err',
  'domain_settings_api_invalid_status',
  'domains_settings_api_unauthorized',
  'domains_settings_reset_failed',
  'domains_status_get_failed',
  'dynamic_form_input_missing',
  'dynamic_form_missing_name',
  'dynamic_form_missing_form_id',
  'dynamic_form_invalid_input_option',
  'dynamic_form_no_forms_receivers',
  'dynamic_form_response_not_found',
  'dynamic_form_not_found',
  'dynamic_input_schema_invlaid_type',
  'dynamic_input_schema_missing_options',
  'dynamic_input_schema_missing',
  'dynamic_input_schema_required',
  'dynamic_input_schema_answer_too_long',
  'latest_releases_perm_err',
  'invalid_task',
  'recommended_resource_perm_err',
] as const;

type ErrorCode = (typeof codes)[number];

interface ApiError {
  code: ErrorCode;
  message: string;
  _unsupported_code?: string;
}

// Intentionally more broadly typed than the array so we can do runtime
// lookups against unknown values without running into type errors.
const codesSet = new Set<string>(codes);

export function ensureError(value: unknown): Error {
  if (value instanceof Error) return value;

  let stringified = '[Unable to stringify the thrown value]';
  try {
    stringified = JSON.stringify(value);
  } catch {
    /* do nothing */
  }

  const error = new Error(
    `This value was thrown as is, not through an Error: ${stringified}`,
  );
  return error;
}

export const ensureApiError = (value: unknown): ApiError => {
  const error = ensureError(value);
  let code: ErrorCode = 'unknown';
  let message: string = '';
  let _unsupported_code: string = '';

  // The typecasting in this function is unfortunate, but it's important
  // we keep stacktraces intact by mutating the error rather than creating
  // a new instance.
  const apiErrorValue = value as ApiError;
  const apiErrorValueNested = value as { data: ApiError };
  if (
    value &&
    typeof value === 'object' &&
    'code' in value &&
    typeof apiErrorValue.code === 'string'
  ) {
    if (codesSet.has(apiErrorValue.code as string)) {
      code = apiErrorValue.code as ErrorCode;
    } else {
      _unsupported_code = apiErrorValue.code;
    }
  }
  if (
    value &&
    typeof value === 'object' &&
    'data' in value &&
    apiErrorValueNested.data &&
    typeof apiErrorValueNested.data === 'object' &&
    'code' in apiErrorValueNested.data &&
    typeof apiErrorValueNested.data.code === 'string'
  ) {
    if (codesSet.has(apiErrorValueNested.data.code as string)) {
      code = apiErrorValueNested.data.code as ErrorCode;
    } else {
      _unsupported_code = apiErrorValueNested.data.code;
    }
  }

  if (
    value &&
    typeof value === 'object' &&
    'message' in value &&
    typeof apiErrorValue.message === 'string'
  ) {
    message = apiErrorValue.message as ErrorCode;
  }
  if (
    value &&
    typeof value === 'object' &&
    'data' in value &&
    apiErrorValueNested.data &&
    typeof apiErrorValueNested.data === 'object' &&
    'message' in apiErrorValueNested.data &&
    typeof apiErrorValueNested.data.message === 'string'
  ) {
    message = apiErrorValueNested.data.message as ErrorCode;
  }

  (error as any as ApiError).code = code;
  if (message) (error as any as ApiError).message = message;
  if (_unsupported_code)
    (error as any as ApiError)._unsupported_code = _unsupported_code;

  return error as any as ApiError;
};
